package com.ives.components.viewer;

import com.ives.modules.vrc.VRC;

import haxe.Timer;

import js.html.MouseEvent;
import js.html.Element;
import js.Browser;
import js.html.DivElement;

import com.er453r.hxcompo.Component;

import com.ives.modules.mpr.MPR;
import com.ives.modules.mip.MIP;
import com.ives.components.productlabel.ProductLabel;
import com.ives.utils.Translator;
import com.ives.modules.tags.Tags;
import com.ives.modules.compare.Compare;
import com.ives.utils.FullScreenUtils;
import com.ives.modules.ModuleEvent;
import com.ives.modules.cine.Cine;
import com.ives.modules.Module;
import com.ives.components.gridpopup.GridPopup;
import com.ives.components.workspace.DicomWorkspace;
import com.ives.utils.Remoting.DicomImageData;
import com.ives.utils.Log;
import com.ives.modules.adjustments.Adjustments;
import com.ives.modules.info.Info;
import com.ives.modules.measurements.Measurements;
import com.ives.modules.roi.ROI;
import com.ives.modules.crosssection.CrossSection;
import com.ives.components.messagehandler.MessageHandler;
import com.ives.modules.ecg.ECG;
import com.ives.utils.Config;

@:allow(com.ives.components.workspace.DicomWorkspace)
@:allow(com.ives.modules.Module)
class DicomViewer extends Component{
	public static inline var LAYOUT_CHANGE:String = "LAYOUT_CHANGE";
	public static inline var WORKSPACE_NORMAL:String = "WORKSPACE_NORMAL";
	public static inline var VIEWER_CLOSE:String = "VIEWER_CLOSE";
	public static inline var RESIZE:String = "RESIZE";
	public static inline var RESIZED:String = "RESIZED";

	public static inline var EVENT_ATTR:String = "data-event";
	public static inline var SYNC_ATTR:String = "data-sync";

	public var originalDicomImageData:DicomImageData; // initial image data
	public var workspaces:Array<DicomWorkspace>; // all workspaces
	private var modules:Array<Module>; // modules used

	public function new(dicomImageData:DicomImageData){
		modules = [
			new Info(),
			new Cine(),
			new Adjustments(),
			new Compare(),
			new Tags(),
			new MIP(),
			new MPR(),
			new CrossSection(),
			new ROI(),
			new Measurements(),
			new VRC(),
			new ECG()
		];

		for(module in modules)
			if(!module.workspaceModule)
				module.init(this);

		Log.debug('Viewer init for image id ${dicomImageData.id}');

		this.originalDicomImageData = dicomImageData;

		on("grid", onGridChange);
		on("moduleevent", onEventFromModule);
		on(".button, .settings input", "click", onButtonClick);
		on("*[data-event]", "click", onEventClick);

		Browser.window.addEventListener("resize", onResize);

		onGridChange([1, 1, -1]); // init layout, -1 not to sync

		if(dicomImageData.numberOfFrames > 1 || dicomImageData.childIds.length == 1)
			reconstructionsButton.style.display = "none";

		if(!FullScreenUtils.fullSreenSupported())
			fullscreen.style.display = "none";

		if(!Config.RECONSTRUCTIONS_ENABLED)
			reconstructionsButton.remove(); // have to remove, since button group will show it again

		if(!Config.ROI_ENABLED)
			roiButton.remove(); // have to remove, since button group will show it again

		if(!Config.BROWSING_ENABLED)
			exitButton.style.display = "none";

		if(!Config.COMPARE_ENABLED)
			compareButton.style.display = "none";

		on('div[data-group="mainGroup"] input', "change", onMainGroupChange);

		on(opener, "click", onMobileOpener);
		on(menu, "click", onMobileMenuClick);
	}

	private function onMainGroupChange(element:Element){
	}

	private function onResize(){
		onEventFromModule(new ModuleEvent(RESIZE, -1));
	}

	private function onButtonClick(element:Element){
		if(element == gridButton)
			new GridPopup().open();

		if(element == fullscreen)
			FullScreenUtils.isFullscreen ? 	FullScreenUtils.exitFullSreen() : FullScreenUtils.enterFullSreen();

		if(element == translateButton)
			Translator.traslateTo(Translator.currentLanguage == "pl" ? "en" : "pl");

		if(element == labelButton)
			new ProductLabel(false).open();

		if(element == colorSwitcher){
			Log.debug('Toggling color mode');

			viewer.classList.contains("dark") ? viewer.classList.remove("dark") : viewer.classList.add("dark");
		}
	}

    private function disabledMeasure() {
        pointButton.checked = false;
        lineButton.checked = false;
        sectionButton.checked = false;
        areaButton.checked = false;
        ellipsisButton.checked = false;
        shapeButton.checked = false;
        angleButton.checked = false;
        cobbButton.checked = false;
        proportionButton.checked = false;
    }

    private function reconstructionsButtonChecked() {
        return mipButton.checked || mprButton.checked || vrcButton.checked || dentalButton.checked;
    }

    private function onEventClick(element:Element) {
        var event = element.getAttribute(EVENT_ATTR);

        if (event == "ADJUSTMENTS_WINDOW_CLICK") {
            panButton.checked = false;
            zoomButton.checked = false;
            rotateButton.checked = false;

            disabledMeasure();
        }

		if (["ADJUSTMENTS_ZOOM_CLICK", "ADJUSTMENTS_PAN_CLICK"].indexOf(event) != -1) {
			windowButton.checked = false;
			rotateButton.checked = false;

			disabledMeasure();
		}

        if (["MIP_MODULE", "MPR_MODULE", "VRC_MODULE", "CROSS_SECTION_MODULE"].indexOf(event) != -1) {
            disabledMeasure();
        }

        if (event == "ADJUSTMENTS_ROTATE_CLICK") {
            panButton.checked = false;
            zoomButton.checked = false;
            windowButton.checked = false;

            disabledMeasure();
        }

		onEventFromModule(new ModuleEvent(element.getAttribute(EVENT_ATTR), selectedWorkspaceId(), false, element.getAttribute(SYNC_ATTR) != "false"));
	}

	private function onGridChange(size:Array<UInt>){
		var modes:Array<String> = [for(n in 0...size[0]*size[1]) WORKSPACE_NORMAL];

		var sync:Bool = true;

		if(size.length > 2)
			sync = false;

		onEventFromModule(new ModuleEvent(DicomViewer.LAYOUT_CHANGE, size, modes, false, sync));
	}

	private function resizeGrid(rows:UInt, columns:UInt, modes:Array<String>){
		Log.debug('Grid event ${rows} x ${columns}');

		clear();
		hideBottomBar();

		ecgButton.style.display = "none";

		workspaces = [];

		var n:UInt = 0;

		for(row in 0...rows){
			var div:DivElement = Browser.document.createDivElement();

			for(column in 0...columns){
				var workspaceModules:Array<Module> = [];

				for(module in modules)
					if(module.workspaceModule)
						workspaceModules.push(module.clone());

				workspaces.push(new DicomWorkspace(n, workspaceModules, this, onEventFromModule, modes[n], originalDicomImageData));

				div.appendChild(workspaces[n++].view);
			}

			append(div);
		}
	}

	public function onEventFromModule(event:ModuleEvent){
		if(event.type == "GRID_MODULE")
			new GridPopup().open();

		Log.debug('Event from module: ${event.type} (origin: ${event.origin})');

		if(!event.external && event.sync)
			MessageHandler.sendEvent(event);

		if(event.type == DicomViewer.LAYOUT_CHANGE)
			resizeGrid(event.data[1], event.data[0], event.strings);
		else if(event.type == DicomViewer.VIEWER_CLOSE)
			close();
		else{
			for(module in modules)
				if(!module.workspaceModule)
					module.handleModuleEvent(event);

			for(workspace in workspaces)
				workspace.onEventFromViewer(event);
		}
	}

	private function selectedWorkspaceId():UInt{
		if(allWorkspaces.checked)
			return -1;

		for(workspace in workspaces)
			if(workspace.selected)
				return workspace.id;

		return -1;
	}

	private function selectedWorkspace():DicomWorkspace{
		for(workspace in workspaces)
			if(workspace.selected)
				return workspace;

		return null;
	}

	private function close(){
		dispatch(VIEWER_CLOSE);
	}

	private function hideBottomBar(){
	}

	private function showBottomBar(){
		onResize();
	}

	private function onMobileOpener(){
		menu.classList.add("show");
		menu.classList.remove("hide");
	}

	private function onMobileMenuClick(event:MouseEvent){
		if(menu.classList.contains("show")){
			var element:Element = cast event.target;

			while(element != null){
				element = element.parentElement;
			}

			menu.classList.remove("show");
			menu.classList.add("hide");
		}
	}
}
